import { Titlebar } from '@components/Titlebar/Titlebar'
import useCreateOrder from '@plug-core/hooks/perps/useCreateOrder'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { sanitizeDecimalInput } from '@plug-core/utils/validationHelper'
import { Banner, Button, Field, IconButton, Input, ListGroup, Surface, Text } from '@ui'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiMinus } from 'react-icons/hi'
import { HiPlus } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'
import OrderInfoItem from './OrderInfoItem'

interface CreateOrderProps {
  hyperliquidTokenId: number
  type?: 'long' | 'short'
  editType?: 'increase' | 'reduce'
  currentOrder?: any
  onClose: () => void
  onSuccess: () => void
}

const CreateOrder: React.FC<CreateOrderProps> = ({
  hyperliquidTokenId,
  type,
  editType,
  currentOrder,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [showAlert, setShowAlert] = useState<string | null>(null)
  const navigate = useNavigate()

  const {
    amount,
    setAmount,
    leverage,
    setLeverage,
    balance,
    error,
    title,
    execute,
    loading,
    perpToken,
    canChangeLeverage,
    executionError,
    sizeLabel,
    liquidationPrice,
    price,
  } = useCreateOrder({
    hyperliquidTokenId,
    type,
    currentOrder,
    editType,
  })

  const rows = useMemo(() => {
    const rows = [
      {
        title: `${perpToken?.name}-USD Price`,
        value: formatMoney(price),
      },
      { title: t('perps.size'), value: sizeLabel },
    ]

    if (liquidationPrice) {
      rows.push({
        title: t('perps.liquidationPrice'),
        value: formatMoney(liquidationPrice),
      })
    }

    return rows
  }, [price, sizeLabel, liquidationPrice, perpToken?.name, t])

  useEffect(() => {
    if (executionError) {
      setShowAlert(executionError)
    }
  }, [executionError])

  const handleLeverageChange = (delta: number) => {
    const newLeverage = Math.max(1, Math.min(perpToken?.maxLeverage || 1, leverage + delta))
    setLeverage(newLeverage)
  }

  const handleLeverageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    sanitizeDecimalInput(value, (corrected) => {
      const numValue = parseInt(corrected)
      const clampedValue = Math.max(1, Math.min(perpToken?.maxLeverage || 1, numValue))
      setLeverage(clampedValue)
    })
  }

  return (
    <div className="mb-3 flex flex-col gap-4">
      <Titlebar title={title} onActionClick={onClose} />

      <Surface padding="none" className="px-4 py-2">
        <Field label={t('perps.margin')} helper={`${t('perps.balance')}: ${formatMoney(balance)}`}>
          <Input
            value={amount ? `$${amount}` : ''}
            onChange={(e) => {
              const value = e.target.value.replace('$', '')
              sanitizeDecimalInput(value, (corrected) => setAmount(corrected))
            }}
            placeholder="$0.00"
            size="lg"
            bare
            className="text-center font-display text-[28px] tabular"
            autoFocus
          />
        </Field>
      </Surface>

      <ListGroup>
        {canChangeLeverage && (
          <OrderInfoItem attribute={t('perps.leverage')}>
            <span className="flex items-center gap-1">
              <IconButton
                variant="secondary"
                size="sm"
                aria-label="-1x"
                disabled={leverage <= 1}
                onClick={() => handleLeverageChange(-1)}
              >
                <HiMinus size={14} />
              </IconButton>
              <Input
                aria-label={t('perps.leverage')}
                value={leverage || ''}
                onChange={handleLeverageInputChange}
                wrapperClassName="min-h-8 w-[64px] gap-0.5 px-2"
                className="py-1 text-center text-[14px] font-semibold tabular"
                trailing={<span className="text-[14px]">x</span>}
              />
              <IconButton
                variant="secondary"
                size="sm"
                aria-label="+1x"
                disabled={leverage >= (perpToken?.maxLeverage || 1)}
                onClick={() => handleLeverageChange(1)}
              >
                <HiPlus size={14} />
              </IconButton>
            </span>
          </OrderInfoItem>
        )}
        {!canChangeLeverage && (
          <OrderInfoItem attribute={t('perps.leverage')}>
            <Text as="span" variant="body-s" weight="semibold" tone="fg">
              {leverage}x
            </Text>
          </OrderInfoItem>
        )}
        {rows.map((row, index) => (
          <OrderInfoItem key={index} attribute={row.title}>
            {row.value}
          </OrderInfoItem>
        ))}
      </ListGroup>

      <Button
        variant="primary"
        fullWidth
        disabled={!!error || loading}
        loading={loading}
        onClick={() => execute().then(() => navigate(-1))}
      >
        {error || title}
      </Button>

      {showAlert && (
        <Banner
          tone="danger"
          className="mt-4"
          onClose={() => setShowAlert(null)}
          closeLabel={t('common.close')}
        >
          {showAlert}
        </Banner>
      )}
    </div>
  )
}

export default CreateOrder
